/**
 * Game Glossary - Single source of truth for all game terms
 * Used for automatic term linking throughout the app
 */

export interface GlossaryTerm {
  name: string;
  aliases: string[];
  definition: string;
  category: 'Mechanic' | 'Condition' | 'Currency' | 'Action' | 'Roll' | 'Status';
  path: string;
  color?: 'hope' | 'fear';
}

export const GLOSSARY: GlossaryTerm[] = [
  // Traits
  {
    name: 'Agility',
    aliases: ['Agility Roll', 'Agility Rolls'],
    definition: 'A high Agility means you\'re fast on your feet, nimble on difficult terrain, and quick to react to danger. You\'ll make an Agility Roll to scurry up a rope, sprint to cover, or bound from rooftop to rooftop.',
    category: 'Mechanic',
    path: '/mechanics?tab=TRAITS#trait-agility',
  },
  {
    name: 'Strength',
    aliases: ['Strength Roll', 'Strength Rolls'],
    definition: 'A high Strength means you\'re better at feats that test your physical prowess and stamina. You\'ll make a Strength Roll to break through a door, lift heavy objects, or hold your ground against a charging foe.',
    category: 'Mechanic',
    path: '/mechanics?tab=TRAITS#trait-strength',
  },
  {
    name: 'Finesse',
    aliases: ['Finesse Roll', 'Finesse Rolls'],
    definition: 'A high Finesse means you\'re skilled at tasks that require accuracy, stealth, or the utmost control. You\'ll make a Finesse Roll to use fine tools, escape notice, or strike with precision.',
    category: 'Mechanic',
    path: '/mechanics?tab=TRAITS#trait-finesse',
  },
  {
    name: 'Instinct',
    aliases: ['Instinct Roll', 'Instinct Rolls'],
    definition: 'A high Instinct means you have a keen sense of your surroundings and a natural intuition. You\'ll make an Instinct Roll to sense danger, notice details in the world around you, or track an elusive foe.',
    category: 'Mechanic',
    path: '/mechanics?tab=TRAITS#trait-instinct',
  },
  {
    name: 'Presence',
    aliases: ['Presence Roll', 'Presence Rolls'],
    definition: 'A high Presence means you have a strong force of personality and a facility for social situations. You\'ll make a Presence Roll to plead your case, intimidate a foe, or capture the attention of a crowd.',
    category: 'Mechanic',
    path: '/mechanics?tab=TRAITS#trait-presence',
  },
  {
    name: 'Knowledge',
    aliases: ['Knowledge Roll', 'Knowledge Rolls'],
    definition: 'A high Knowledge means you know information others don\'t and understand how to apply your mind through deduction and inference. You\'ll make a Knowledge Roll to interpret facts, see the patterns clearly, or remember important information.',
    category: 'Mechanic',
    path: '/mechanics?tab=TRAITS#trait-knowledge',
  },

  // Currencies
  {
    name: 'Hope',
    aliases: ['Hope Die', 'Hope Token'],
    definition: 'Every PC starts with 2 Hope at character creation and gains more throughout play. A PC can have a maximum of 6 Hope at one time, and Hope carries over between sessions.',
    category: 'Currency',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#hope',
    color: 'hope',
  },
  {
    name: 'Fear',
    aliases: ['Fear Die', 'Fear Token'],
    definition: 'The GM gains Fear whenever a player rolls with Fear and can spend Fear at any time to make or enhance a GM move or to use a Fear Feature. The GM can have up to 12 Fear at one time. Fear carries over between sessions.',
    category: 'Currency',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#fear',
    color: 'fear',
  },
  {
    name: 'Duality Dice',
    aliases: ['Duality', 'Hope and Fear Dice'],
    definition: 'All action rolls require a pair of d12s called Duality Dice. These are two visually distinct twelve-sided dice, with one die representing Hope and the other representing Fear.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#action-rolls',
  },
  {
    name: 'Stress',
    aliases: ['Stress Slots', 'Mental Damage'],
    definition: 'Stress represents how much mental, physical, and emotional strain a character can endure. All classes start with 6 Stress slots. You\'ll be able to increase the number of Stress slots you have available as you level up, to a maximum of 12.',
    category: 'Status',
    path: '/mechanics?tab=STRESS',
  },

  // Core Mechanics
  {
    name: 'Action Roll',
    aliases: ['Action Rolls', 'Roll'],
    definition: 'Any move where success would be trivial or failure would be boring automatically succeeds, but any move that\'s difficult to accomplish or risky to attempt triggers an Action Roll.',
    category: 'Roll',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#action-rolls',
  },
  {
    name: 'Trait Roll',
    aliases: ['Trait Rolls'],
    definition: 'An action roll that specifies which character Trait applies to it is called a Trait roll. In the text of a feature or effect, a Trait roll is referenced with the format "[Trait] Roll (Difficulty)" (e.g., "Agility Roll (12)").',
    category: 'Roll',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#trait-rolls',
  },
  {
    name: 'Reaction Roll',
    aliases: ['Reaction Rolls', 'Reaction'],
    definition: 'A reaction roll is made in response to an attack or a hazard, representing a character\'s attempt to avoid or withstand an imminent effect. Reaction rolls work like action rolls, except they don\'t generate Hope or Fear.',
    category: 'Roll',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#reaction-rolls',
  },
  {
    name: 'Spellcast Roll',
    aliases: ['Spellcast Rolls', 'Spell Roll', 'Casting'],
    definition: 'Spellcast rolls are Trait rolls that require you to use your Spellcast Trait. Your Spellcast Trait, if you have one, is determined by your subclass. Spellcast rolls are only made when a character uses a feature that requires one.',
    category: 'Roll',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#spellcast-rolls',
  },
  {
    name: 'Group Action Roll',
    aliases: ['Group Action Rolls', 'Group Action'],
    definition: 'When multiple PCs take action together, the party chooses one PC to lead the action. Each other player then describes how their character collaborates on the task. The leader makes an action roll as usual, while the other players make reaction rolls.',
    category: 'Roll',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#group-action-rolls',
  },
  {
    name: 'Tag Team Roll',
    aliases: ['Tag Team Rolls', 'Tag Team'],
    definition: 'Each player can, once per session, initiate a Tag Team Roll between their character and another PC by spending 3 Hope. Both players make separate action rolls; before resolving the roll\'s outcome, choose one of the rolls to apply to both actions.',
    category: 'Roll',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#tag-team-rolls',
  },
  {
    name: 'Critical Success',
    aliases: ['Crit', 'Critical', 'Matching Dice'],
    definition: 'If the Duality Dice show matching results, you rolled a Critical Success. You automatically succeed with a bonus, gain a Hope, and clear a Stress. If this was an Attack Roll, you deal critical damage.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#action-rolls',
  },
  {
    name: 'Failing Forward',
    aliases: ['Failing', 'Failure', 'Fail Forward'],
    definition: 'In Daggerheart, every time you roll the dice, the scene changes in some way. There is no such thing as a roll where "nothing happens," because the fiction constantly evolves based on the successes and failures of the characters.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#failing-forward',
  },
  {
    name: 'Advantage',
    aliases: ['Advantage Die', 'Extra Die'],
    definition: 'Advantage represents an opportunity that you seize to increase your chances of success. When you roll with Advantage, you roll a d6 advantage die with your dice pool and add its result to your total.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#advantage-disadvantage',
  },
  {
    name: 'Disadvantage',
    aliases: ['Disadvantage Die'],
    definition: 'Disadvantage represents an additional difficulty, hardship, or challenge you face when attempting an action. When you roll with Disadvantage, you roll a d6 disadvantage die with your dice pool and subtract its result from your total.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAKING+MOVES+%26+TAKING+ACTION#advantage-disadvantage',
  },

  // Combat
  {
    name: 'Evasion',
    aliases: ['Evasion Score', 'Defense Score'],
    definition: 'Evasion represents a character\'s ability to avoid attacks and other unwanted effects. Any roll made against a PC has a Difficulty equal to the target\'s Evasion. A PC\'s base Evasion is determined by their class, but can be modified by Domain Cards, equipment, Conditions, and other effects.',
    category: 'Mechanic',
    path: '/mechanics?tab=COMBAT#evasion',
  },
  {
    name: 'Hit Points',
    aliases: ['HP', 'Hit Point', 'Health'],
    definition: 'Hit Points (HP) represent a character\'s ability to withstand physical injury. When a character takes damage, they mark 1 to 3 Hit Points, based on their damage thresholds. You\'ll be able to increase the number of Hit Points you have available as you level up, to a maximum of 12.',
    category: 'Status',
    path: '/mechanics?tab=COMBAT#hit-points-damage-thresholds',
  },
  {
    name: 'Damage Thresholds',
    aliases: ['Thresholds', 'Damage Threshold'],
    definition: 'If the final damage is at or above the Severe threshold, mark 3 Hit Points. At or above Major (but below Severe), mark 2. Below Major, mark 1. If incoming damage is reduced to 0 or less, no Hit Points are marked.',
    category: 'Mechanic',
    path: '/mechanics?tab=COMBAT#hit-points-damage-thresholds',
  },
  {
    name: 'Attack Roll',
    aliases: ['Attack Rolls', 'Attack'],
    definition: 'An Attack Roll is an action roll intended to inflict harm. The trait that applies to an Attack Roll is specified by the weapon or spell being used. An Attack Roll\'s Difficulty, unless otherwise noted, is equal to the Difficulty score of its target.',
    category: 'Roll',
    path: '/mechanics?tab=ATTACKING#attack-rolls',
  },
  {
    name: 'Damage Roll',
    aliases: ['Damage Rolls', 'Damage'],
    definition: 'On a successful attack, roll damage. Damage is calculated from the damage roll listed in the attack\'s description with the format "xdy+[modifier]" (e.g., for a spell that inflicts "1d8+2" damage, you roll an eight-sided die and add 2 to the result).',
    category: 'Roll',
    path: '/mechanics?tab=ATTACKING#damage-rolls',
  },
  {
    name: 'Critical Damage',
    aliases: ['Critical', 'Extra Damage'],
    definition: 'When you get a Critical Success on an Attack Roll, you deal extra damage. Make the damage roll as usual, but add the maximum possible result of the damage dice to the final total.',
    category: 'Mechanic',
    path: '/mechanics?tab=ATTACKING#critical-damage',
  },
  {
    name: 'Resistance',
    aliases: ['Damage Resistance', 'Resistant'],
    definition: 'If a target has resistance to a damage type, then they reduce incoming damage of that type by half before comparing it to their Hit Point Thresholds. The effects of multiple resistances to the same damage type do not stack.',
    category: 'Mechanic',
    path: '/mechanics?tab=ATTACKING#resistance-immunity-direct-damage',
  },
  {
    name: 'Immunity',
    aliases: ['Immune', 'Damage Immunity'],
    definition: 'If a target has immunity to a damage type, they ignore incoming damage of that type.',
    category: 'Mechanic',
    path: '/mechanics?tab=ATTACKING#resistance-immunity-direct-damage',
  },
  {
    name: 'Direct Damage',
    aliases: ['Direct'],
    definition: 'Direct damage is damage that can\'t be reduced by marking Armor Slots.',
    category: 'Mechanic',
    path: '/mechanics?tab=ATTACKING#resistance-immunity-direct-damage',
  },

  // Conditions
  {
    name: 'Hidden',
    aliases: ['Concealed', 'Invisible'],
    definition: 'While you\'re out of sight from all enemies and they don\'t otherwise know your location, you gain the Hidden Condition. Any rolls against a Hidden creature have Disadvantage. After an adversary moves to where they would see you, you move into their line of sight, or you make an Attack Roll, you are no longer Hidden.',
    category: 'Condition',
    path: '/mechanics?tab=CONDITIONS#condition-hidden',
  },
  {
    name: 'Restrained',
    aliases: ['Grappled', 'Restrain'],
    definition: 'Restrained characters can\'t move, but you can still take actions from their current position.',
    category: 'Condition',
    path: '/mechanics?tab=CONDITIONS#condition-restrained',
  },
  {
    name: 'Vulnerable',
    aliases: ['Vulnerable Condition', 'Vulnerability'],
    definition: 'When a creature is Vulnerable, all rolls targeting them have Advantage.',
    category: 'Condition',
    path: '/mechanics?tab=CONDITIONS#condition-vulnerable',
  },
  {
    name: 'Temporary Tags',
    aliases: ['Temporary Condition', 'Tag'],
    definition: 'The temporary tag denotes a Condition or effect that the affected creature can clear by making a move against it. When an affected PC makes a move to clear a temporary Condition, it normally requires a successful action roll using an appropriate trait.',
    category: 'Condition',
    path: '/mechanics?tab=CONDITIONS#temporary-tags',
  },

  // Range & Movement
  {
    name: 'Movement',
    aliases: ['Movement', 'Move'],
    definition: 'When you\'re under pressure or in danger and make an action roll, you can move to a location within Close range as part of that action. If you\'re not already making an action roll, or if you want to move farther than your Close range, you need to succeed on an Agility Roll to safely reposition yourself.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAPS%2C+RANGE%2C+AND+MOVEMENT#movement-under-pressure',
  },
  {
    name: 'Area of Effect',
    aliases: ['AOE', 'AoE', 'Blast', 'Burst'],
    definition: 'Unless stated otherwise, all the targets of a group effect must be within Very Close range of a single origin point within your effect\'s range.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAPS%2C+RANGE%2C+AND+MOVEMENT#area-of-effect',
  },
  {
    name: 'Line of Sight',
    aliases: ['Sight', 'LOS'],
    definition: 'Unless stated otherwise, a ranged attacker must have line of sight to their intended target to make an Attack Roll. If a partial obstruction lies between the attacker and target, the target has cover. If the obstruction is total, there is no line of sight.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAPS%2C+RANGE%2C+AND+MOVEMENT#line-of-sight-cover',
  },
  {
    name: 'Cover',
    aliases: ['Covered', 'Obstruction'],
    definition: 'If a partial obstruction lies between the attacker and target, the target has cover. Attack Rolls made through cover are rolled with Disadvantage.',
    category: 'Mechanic',
    path: '/mechanics?tab=MAPS%2C+RANGE%2C+AND+MOVEMENT#line-of-sight-cover',
  },

  // Downtime & Consequences
  {
    name: 'Downtime',
    aliases: ['Rest', 'Short Rest', 'Long Rest'],
    definition: 'Between conflicts, the party can take a rest to recover expended resources and deepen their bonds. During a rest, each PC can make up to two downtime moves.',
    category: 'Mechanic',
    path: '/mechanics?tab=DOWNTIME#downtime-consequences',
  },
  {
    name: 'Short Rest',
    aliases: ['Short'],
    definition: 'A Short Rest lasts enough time for the party to catch its breath, about an hour in-world. Each player can move domain cards between their Loadout and Vault for free, then choose twice from the list of downtime moves.',
    category: 'Mechanic',
    path: '/mechanics?tab=DOWNTIME',
  },
  {
    name: 'Long Rest',
    aliases: ['Long'],
    definition: 'A Long Rest is when the characters make camp and relax or sleep for several in-game hours. Each player can move domain cards between their Loadout and Vault for free, then choose twice from the list of downtime moves.',
    category: 'Mechanic',
    path: '/mechanics?tab=DOWNTIME',
  },

  // Death
  {
    name: 'Fall',
    aliases: ['Fallen', 'Falling'],
    definition: 'When a PC marks their last Hit Point, they must make a death move by choosing one of the following options: Blaze of Glory, Avoid Death, or Risk It All.',
    category: 'Mechanic',
    path: '/mechanics?tab=DEATH',
  },
  {
    name: 'Blaze of Glory',
    aliases: ['Glory'],
    definition: 'Your character embraces death and goes out in a blaze of glory. Take one final action. It automatically critically succeeds (with GM approval), and then you cross through the veil of death.',
    category: 'Action',
    path: '/mechanics?tab=DEATH',
  },
  {
    name: 'Avoid Death',
    aliases: ['Avoid'],
    definition: 'Your character avoids death and faces the consequences. They temporarily drop unconscious, and then you work with the GM to describe how the situation worsens. They return to consciousness when an ally clears 1 or more of their marked Hit Points or when the party finishes a Long Rest.',
    category: 'Action',
    path: '/mechanics?tab=DEATH',
  },
  {
    name: 'Risk It All',
    aliases: ['Risk'],
    definition: 'Roll your Duality Dice. If the Hope Die is higher, your character stays on their feet and clears a number of Hit Points or Stress equal to the value of the Hope Die. If the Fear Die is higher, your character crosses through the veil of death. If the Duality Dice show matching results, your character stays up and gains an additional benefit at the GM\'s discretion.',
    category: 'Action',
    path: '/mechanics?tab=DEATH',
  },

  // Leveling
  {
    name: 'Level Up',
    aliases: ['Advance', 'Advancement', 'Leveling'],
    definition: 'Your party levels up whenever the GM decides you\'ve reached a narrative milestone (usually about every 3 sessions). All party members level up at the same time.',
    category: 'Mechanic',
    path: '/mechanics?tab=LEVELING+UP',
  },
  {
    name: 'Tier',
    aliases: ['Tier Level'],
    definition: 'Daggerheart has 10 PC levels divided into 4 tiers: Tier 1 (level 1), Tier 2 (levels 2–4), Tier 3 (levels 5–7), Tier 4 (levels 8–10). Your tier affects your damage thresholds, tier achievements, and access to advancements.',
    category: 'Mechanic',
    path: '/mechanics?tab=LEVELING+UP#step-damage-thresholds',
  },
  {
    name: 'Domain Card',
    aliases: ['Domain', 'Ability', 'Domain Cards', 'domain cards', 'Domains'],
    definition: 'Acquire a new Domain Card at your level or lower from one of your class\'s domains and add it to your Loadout or Vault. You can also exchange one Domain Card you\'ve previously acquired for a different Domain Card of the same level or lower.',
    category: 'Mechanic',
    path: '/mechanics?tab=LEVELING+UP#step-domain-cards',
  },

  // Heritage
  {
    name: 'Ancestry',
    aliases: ['ancestry', 'ancestries', 'Ancestries'],
    definition: 'Your character\'s lineage, granting two unique ancestry features. Mixed ancestry combines features from two different ancestries.',
    category: 'Mechanic',
    path: '/ancestries',
  },
  {
    name: 'Community',
    aliases: ['community', 'communities', 'Communities'],
    definition: 'Your character\'s cultural upbringing, granting a community feature that reflects the society that shaped them.',
    category: 'Mechanic',
    path: '/communities',
  },

  // Equipment
  {
    name: 'Weapon',
    aliases: ['weapon', 'weapons', 'Weapons'],
    definition: 'All weapons have a tier, trait, range, damage die, damage type, and burden. Some weapons also have a feature.',
    category: 'Mechanic',
    path: '/equipment?tab=weapons',
  },

  // Rules
  {
    name: 'Proficiency',
    aliases: ['Proficiency Bonus', 'Prof'],
    definition: 'For weapons, the number of damage dice you roll is equal to your Proficiency. Proficiency multiplies the number of dice you roll, but doesn\'t affect the modifier.',
    category: 'Mechanic',
    path: '/mechanics?tab=ATTACKING#damage-rolls',
  },
  {
    name: 'Armor Slots',
    aliases: ['Armor', 'Armor Slot'],
    definition: 'When you take damage, you can mark one Armor Slot to reduce the severity of the damage by one threshold (Severe to Major, Major to Minor, Minor to Nothing). If your character has an Armor Score of 0, you can\'t mark Armor Slots.',
    category: 'Status',
    path: '/mechanics?tab=EQUIPMENT+#armor',
  },
  {
    name: 'Stacking Effects',
    aliases: ['Stacking', 'Multiple Effects'],
    definition: 'Unless stated otherwise, all effects beside Conditions and Advantage/Disadvantage can stack.',
    category: 'Mechanic',
    path: '/mechanics?tab=DEATH#stacking-effects',
  },
  {
    name: 'Ongoing Spell Effects',
    aliases: ['Ongoing Effect', 'Persistent Spell'],
    definition: 'If an effect doesn\'t have a listed mechanical expiration, it only ends when decided by the controlling player, the GM, or the demands of the fiction.',
    category: 'Mechanic',
    path: '/mechanics?tab=DEATH#ongoing-spell-effects',
  },
  {
    name: 'Rounding',
    aliases: ['Round', 'Round Up'],
    definition: 'This game doesn\'t use fractions; if you need to round to a whole number, round up unless otherwise specified. When in doubt, resolve any ambiguity in favor of the PCs.',
    category: 'Mechanic',
    path: '/mechanics?tab=ADDITIONAL+RULES#rounding-up',
  },
];

/**
 * Look up a term by name or alias (case-insensitive)
 */
export function getTermByName(name: string): GlossaryTerm | undefined {
  const lower = name.toLowerCase().trim();
  return GLOSSARY.find(
    (t) =>
      t.name.toLowerCase() === lower ||
      t.aliases.some((a) => a.toLowerCase() === lower)
  );
}

/**
 * Search glossary for terms matching a query
 */
export function searchGlossary(query: string): GlossaryTerm[] {
  const lower = query.toLowerCase().trim();
  if (!lower) return [];

  return GLOSSARY.filter(
    (t) =>
      t.name.toLowerCase().includes(lower) ||
      t.definition.toLowerCase().includes(lower) ||
      t.aliases.some((a) => a.toLowerCase().includes(lower))
  );
}
